import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { StreamingActivity } from 'app/model/pipeline/streaming.activity';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { FilePath } from 'app/model/dataset/file-path';
import { ValueAdapter } from './../value.adapter';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(StreamingActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            storageLinkedServices: linkedServiceReferenceWithoutParameterizationRemainer,
            arguments: '*',
            getDebugInfo: '*',
            mapper: '*',
            reducer: '*',
            input: '*',
            output: '*',
            filePaths: '*',
            fileLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            defines: '*'
        }
    }
})
export class HDInsightStreamingActivityAdapter extends ActivityAsyncAdapter<StreamingActivity, Model.HDInsightStreamingActivity> {

    public model(): IModel {
        return Metamodel.modelFor(StreamingActivity);
    }

    protected async _toResource(activity: StreamingActivity): Promise<Model.HDInsightStreamingActivity> {
        const modelHDInsightStreamingActivity: Model.HDInsightStreamingActivity = super._getCommonActivityProperties(activity, 'HDInsightStreaming') as Model.HDInsightStreamingActivity;
        const streamingActivity: StreamingActivity = activity as StreamingActivity;

        modelHDInsightStreamingActivity.typeProperties = {
            mapper: ValueAdapter.toResource(streamingActivity, StreamingActivity.MAPPER),
            reducer: ValueAdapter.toResource(streamingActivity, StreamingActivity.REDUCER),
            filePaths: [streamingActivity.mapperFilePath.path, streamingActivity.reducerFilePath.path],
            input: ValueAdapter.toResource(streamingActivity, StreamingActivity.INPUT),
            output: ValueAdapter.toResource(streamingActivity, StreamingActivity.OUTPUT),
            arguments: toArgsArray(streamingActivity.streamingActivityArguments, streamingActivity, StreamingActivity.ARGUMENTS),
            getDebugInfo: streamingActivity.getDebugInfo,
            defines: toParameterObject(streamingActivity.defines)
        };

        if (streamingActivity.fileLinkedService) {
            modelHDInsightStreamingActivity.typeProperties.fileLinkedService = this._toLinkedServiceReferenceModel(streamingActivity.fileLinkedService);
        }

        if (streamingActivity.linkedService) {
            modelHDInsightStreamingActivity.linkedServiceName = this._toLinkedServiceReferenceModel(streamingActivity.linkedService);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on hive activity
            _.merge(modelHDInsightStreamingActivity, parsed);
        }

        return modelHDInsightStreamingActivity;
    }

    protected async _toModel(modelActivity: Model.HDInsightStreamingActivity): Promise<StreamingActivity> {
        const activity: StreamingActivity = new StreamingActivity(modelActivity.name);
        const modelStreamingActivity = modelActivity as Model.HDInsightStreamingActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelStreamingActivity.typeProperties) {
            ValueAdapter.toModel(activity, StreamingActivity.MAPPER, modelStreamingActivity.typeProperties.mapper);
            ValueAdapter.toModel(activity, StreamingActivity.REDUCER, modelStreamingActivity.typeProperties.reducer);
            ValueAdapter.toModel(activity, StreamingActivity.INPUT, modelStreamingActivity.typeProperties.input);
            ValueAdapter.toModel(activity, StreamingActivity.OUTPUT, modelStreamingActivity.typeProperties.output);
            const args = toArgsString(activity, modelStreamingActivity.typeProperties.arguments, StreamingActivity.ARGUMENTS);
            if (args) {
                activity.streamingActivityArguments = args;
            }
            activity.getDebugInfo = modelStreamingActivity.typeProperties.getDebugInfo;
            activity.defines = toParameterClass(modelStreamingActivity.typeProperties.defines, activity);

            if (modelStreamingActivity.typeProperties.filePaths == null) {
                activity.mapperFilePath = new FilePath();
                activity.reducerFilePath = new FilePath();
            } else {
                [activity.mapperFilePath.path, activity.reducerFilePath.path] = modelStreamingActivity.typeProperties.filePaths;
                activity.mapperFilePath.path = activity.mapperFilePath.path || '';
                activity.reducerFilePath.path = activity.reducerFilePath.path || '';
            }

            if (modelStreamingActivity.typeProperties.fileLinkedService !== undefined) {
                activity.fileLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                    modelStreamingActivity.typeProperties.fileLinkedService, this.context);
            }
        }

        if (modelStreamingActivity.linkedServiceName !== undefined) {
            activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(HDInsightLinkedService),
                modelStreamingActivity.linkedServiceName, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelStreamingActivity, StreamingActivity.MODEL));

        return activity;
    }
}
